import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { StorageUnit, StorageUnitInput } from '../../types/storageUnits';
import { StorageUnitType } from '../../types/Enums';
import { translateStorageUnitType } from '../../types/EnumTranslations';
import { LevelModel } from '../../types/protocols';
import { levelDisplayName } from '../../Helpers/levelDisplayName';
import { duplicateUnitLevelName, storageLevelsDescending } from '../../Helpers/storageUnitUtils';
import RemovePlacementField from '../../Components/RentableDetail/RemovePlacementField';
import { RentablePhotoActions, RentablePhotosField, useStagedPhotos } from '../../Components/RentableDetail/RentablePhotosField';

interface StorageUnitModalProps {
    show: boolean;
    onHide: () => void;
    onSubmit: (input: StorageUnitInput) => Promise<void>;
    levels: { [key: string]: LevelModel };
    /** Every unit in the building, for the duplicate-number warning. */
    allUnits: StorageUnit[];
    /** When given, the modal edits this unit; otherwise it creates one. */
    unit?: StorageUnit;
    /** Pre-filled values for a new unit (series placement, selected level). */
    initialValues?: Partial<StorageUnitInput>;
    /**
     * Level whose drawing was clicked to open this modal. The clicked position
     * only applies to that level; picking another level creates the unit
     * without a position, so the modal warns about it.
     */
    placementLevelKey?: string;
    submitting?: boolean;
    /** Editing a unit with a marker on the current drawing: offers "Ta bort placering", run after the unit is saved. */
    onRemoveLocation?: () => Promise<void>;
    /** Editing: the Foton field; changes apply after the object is saved, so Avbryt changes nothing. */
    photoActions?: RentablePhotoActions;
}

/**
 * Create/edit form for a storage unit. Area in m², ceiling height entered in
 * mm and stored in metres with the level's height as placeholder. A number
 * already used on the same level shows a warning but saves (plan D15).
 */
const StorageUnitModal: React.FC<StorageUnitModalProps> = ({
    show, onHide, onSubmit, levels, allUnits, unit, initialValues, placementLevelKey, submitting = false, onRemoveLocation, photoActions
}) => {
    const stagedPhotos = useStagedPhotos('storage_unit');
    const { reset: resetPhotos } = stagedPhotos;
    useEffect(() => {
        if (show) resetPhotos();
    }, [show, resetPhotos]);
    const busy = submitting || stagedPhotos.busy;
    const isEditing = !!unit;
    const [number, setNumber] = useState('');
    const [type, setType] = useState<StorageUnitType>(StorageUnitType.Mesh);
    const [levelKey, setLevelKey] = useState('');
    const [areaText, setAreaText] = useState('');
    const [ceilingHeightMm, setCeilingHeightMm] = useState('');
    const [comment, setComment] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [removeLocation, setRemoveLocation] = useState(false);

    const sortedLevels = useMemo(() => storageLevelsDescending(levels), [levels]);

    useEffect(() => {
        if (!show) return;
        const source: Partial<StorageUnitInput> = unit || initialValues || {};
        setNumber(source.number || '');
        setType(source.type || StorageUnitType.Mesh);
        setLevelKey(source.levelKey || sortedLevels[sortedLevels.length - 1]?.key || '');
        setAreaText(source.area !== undefined ? String(source.area).replace('.', ',') : '');
        setCeilingHeightMm(source.ceilingHeight ? String(Math.round(source.ceilingHeight * 1000)) : '');
        setComment(source.comment || '');
        setRemoveLocation(false);
        setError(null);
    }, [show, unit, initialValues, sortedLevels]);

    const level = levelKey ? levels[levelKey] : undefined;
    const levelDefaultMm = level?.ceilingHeight ? Math.round(level.ceilingHeight * 1000) : undefined;
    const placementLevel = placementLevelKey ? levels[placementLevelKey] : undefined;
    const placementDiscarded = !!placementLevel && levelKey !== placementLevelKey;
    const duplicateOn = useMemo(
        () => (levelKey ? duplicateUnitLevelName(allUnits, levels, levelKey, number, unit?.key) : null),
        [allUnits, levels, levelKey, number, unit?.key]
    );

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!number.trim()) {
            setError('Förrådsnummer krävs');
            return;
        }
        if (!levelKey) {
            setError('Våning krävs');
            return;
        }
        let area: number | undefined;
        if (areaText.trim()) {
            const parsed = parseFloat(areaText.replace(',', '.'));
            if (!Number.isFinite(parsed) || parsed <= 0) {
                setError('Yta måste vara ett positivt antal kvadratmeter');
                return;
            }
            area = Math.round(parsed * 10) / 10;
        }
        let ceilingHeight: number | undefined;
        if (ceilingHeightMm.trim()) {
            const mm = parseInt(ceilingHeightMm, 10);
            if (!Number.isFinite(mm) || mm <= 0) {
                setError('Takhöjd måste vara ett positivt antal millimeter');
                return;
            }
            ceilingHeight = mm / 1000;
        }

        try {
            await onSubmit({
                number: number.trim(),
                type,
                levelKey,
                area,
                ceilingHeight,
                comment: comment.trim() || undefined,
            });
            if (removeLocation && onRemoveLocation) await onRemoveLocation();
            if (isEditing && photoActions) await stagedPhotos.apply(photoActions);
            onHide();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Kunde inte spara förrådet');
        }
    };

    return (
        <Modal show={show} onHide={busy ? () => undefined : onHide} centered>
            <Form onSubmit={handleSubmit}>
                <Modal.Header closeButton>
                    <Modal.Title>{isEditing ? 'Redigera förråd' : 'Nytt förråd'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}

                    <Form.Group className="mb-3">
                        <Form.Label>Förrådsnummer *</Form.Label>
                        <Form.Control
                            type="text"
                            value={number}
                            onChange={e => setNumber(e.target.value.toUpperCase())}
                            placeholder="T.ex. 12 eller K12"
                            autoFocus
                        />
                        {duplicateOn && (
                            <Form.Text className="text-warning d-block">Numret används redan på {duplicateOn}.</Form.Text>
                        )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Typ</Form.Label>
                        <Form.Select value={type} onChange={e => setType(e.target.value as StorageUnitType)}>
                            {Object.values(StorageUnitType).map(value => (
                                <option key={value} value={value}>{translateStorageUnitType(value)}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Våning *</Form.Label>
                        <Form.Select value={levelKey} onChange={e => setLevelKey(e.target.value)} disabled={sortedLevels.length === 0}>
                            {sortedLevels.length === 0 && <option value="">Inga våningsplan</option>}
                            {sortedLevels.map(l => (
                                <option key={l.key} value={l.key}>{levelDisplayName(l)}</option>
                            ))}
                        </Form.Select>
                        {placementDiscarded ? (
                            <Form.Text className="text-warning d-block">
                                Den klickade placeringen gäller ritningen för {levelDisplayName(placementLevel)}. Förrådet skapas på vald våning utan placering.
                            </Form.Text>
                        ) : (
                            <Form.Text muted>Förrådet placeras på våningens ritning.</Form.Text>
                        )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Yta (m²)</Form.Label>
                        <Form.Control
                            type="text"
                            inputMode="decimal"
                            value={areaText}
                            onChange={e => setAreaText(e.target.value)}
                            placeholder="T.ex. 4,5"
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Takhöjd (mm)</Form.Label>
                        <Form.Control
                            type="number"
                            min={1}
                            value={ceilingHeightMm}
                            onChange={e => setCeilingHeightMm(e.target.value)}
                            placeholder={levelDefaultMm ? `${levelDefaultMm} (våningens)` : 'Valfritt'}
                        />
                        <Form.Text muted>Lämna tomt för att använda våningens takhöjd.</Form.Text>
                    </Form.Group>

                    {isEditing && onRemoveLocation && (
                        <RemovePlacementField marked={removeLocation} onChange={setRemoveLocation} hint="Förrådet finns kvar i listan och kan placeras igen." />
                    )}

                    {isEditing && photoActions && <RentablePhotosField actions={photoActions} staged={stagedPhotos} />}

                    <Form.Group className="mb-1">
                        <Form.Label>Kommentar</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            value={comment}
                            onChange={e => setComment(e.target.value)}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={busy}>Avbryt</Button>
                    <Button type="submit" variant="primary" disabled={busy || sortedLevels.length === 0}>
                        {busy ? <><Spinner size="sm" animation="border" /> Sparar...</> : 'Spara'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default StorageUnitModal;
